import { Link, useLocation } from "react-router-dom";
import { AlertTriangle, ArrowRight, ShieldOff, SplitSquareVertical } from "lucide-react";

import { FilterBar } from "../components/FilterBar";
import { PageHeader } from "../components/PageHeader";
import {
  AllRulesBarChart,
  ComplianceTrendChart,
} from "../components/PolicyComplianceCharts";
import { RuleTable, type RuleRow } from "../components/RuleTable";
import { StepShell, getStep } from "../components/StepShell";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../ui/card";
import { Skeleton } from "../../../ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "../../../ui/toggle-group";
import { usePolicyComplianceSummary } from "../lib/api/policy-compliance";
import type { PolicyComplianceSummary, RuleId, SegmentFilter } from "../lib/api/types";
import { useDashboardSearch } from "../lib/useDashboardSearch";

const SUBTITLE =
  "Which policy conditions are being waived, how often, and whether it's confirmed or still a candidate.";

const SEGMENTS: { value: SegmentFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "PG", label: "PG" },
  { value: "Corp", label: "Corp" },
];

function EmptyState() {
  return (
    <Card className="border-neutral-inactive/40">
      <CardContent className="flex flex-col items-center gap-3 px-6 py-16 text-center">
        <ShieldOff className="size-8 text-neutral-inactive" />
        <p className="text-base font-semibold text-neutral-inactive">
          No data — not yet live on Athena
        </p>
        <p className="max-w-xl text-sm leading-relaxed text-muted-foreground">
          Rule-level compliance is captured from the moment an office goes live. Select the live
          office or &ldquo;All Offices&rdquo; to see this data.
        </p>
      </CardContent>
    </Card>
  );
}

const GATE_LABEL: Record<"DQ1" | "DQ2", string> = {
  DQ1: "zero/blank amount",
  DQ2: "guarantor on file, no usable FICO",
};

function GateBanner({ data }: { data: PolicyComplianceSummary }) {
  return (
    <div className="rounded-lg border border-neutral-inactive/40 bg-neutral-inactive-dim px-4 py-3">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 size-4 shrink-0 text-neutral-inactive" />
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wider text-neutral-inactive">
            Prerequisite gates — excluded before any rule is evaluated
          </p>
          <div className="flex flex-wrap gap-x-8 gap-y-2">
            {data.gates.map((gate) => (
              <div key={gate.ruleId}>
                <p className="text-sm text-foreground">
                  <span className="font-semibold tabular-nums">
                    {gate.excludedCount.toLocaleString()}
                  </span>{" "}
                  deals excluded — {GATE_LABEL[gate.ruleId]}
                </p>
                <p className="text-[11px] tabular-nums text-muted-foreground">
                  {gate.ruleId} · {gate.excludedPctOfTotal.toFixed(2)}% of the period&rsquo;s
                  population
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function rowsFor(data: PolicyComplianceSummary, ids: RuleId[]): RuleRow[] {
  return ids.flatMap((id) => {
    const definition = data.ruleDefinitions.find((d) => d.ruleId === id);
    const metric = data.ruleMetrics.find((m) => m.ruleId === id);
    return definition && metric ? [{ definition, metric }] : [];
  });
}

export function PolicyCompliance() {
  const location = useLocation();
  const { range, office, compare, segment, update } = useDashboardSearch();

  const { data, isPending, isError, error } = usePolicyComplianceSummary(segment, {
    ...(range ? { range } : {}),
    ...(office ? { office } : {}),
    ...(compare != null ? { compare } : {}),
  });

  const setSegment = (value: string) => {
    if (!value) return;
    update({ segment: value as SegmentFilter });
  };

  return (
    <StepShell step={getStep("policy-compliance")}>
      <PageHeader title="Policy Compliance by Rule" subtitle={SUBTITLE} />
      <FilterBar />

      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card p-3">
        <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          <SplitSquareVertical className="size-4 text-primary" />
          Segment
        </span>
        <ToggleGroup
          type="single"
          value={segment}
          onValueChange={setSegment}
          variant="outline"
          size="sm"
          aria-label="PG / Corp segment"
        >
          {SEGMENTS.map((s) => (
            <ToggleGroupItem key={s.value} value={s.value} className="px-4">
              {s.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <span className="text-xs text-muted-foreground">
          Applies to the gates, the compliance trend, and every rule count on this page.
        </span>
      </div>

      {isError ? (
        <Card>
          <CardContent className="p-6 text-sm text-decline" role="alert">
            Could not load policy compliance data: {(error as Error).message}
          </CardContent>
        </Card>
      ) : isPending || !data ? (
        <div className="space-y-4">
          <Skeleton className="h-[92px] rounded-lg" />
          <Skeleton className="h-[460px] rounded-lg" />
        </div>
      ) : !data.dataAvailable ? (
        <EmptyState />
      ) : (
        <>
          {/* Section 1 — Prerequisite gates */}
          <GateBanner data={data} />

          {/* Section 2 — Compliance rate by credit grade */}
          <Card>
            <CardHeader>
              <CardTitle>Policy Compliance Rate by Tier</CardTitle>
              <CardDescription>
                Share of approved deals with no policy exception, split by the deal&rsquo;s credit
                grade (A / B1 / B2 / B3).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <ComplianceTrendChart series={data.complianceTrend} />
              <p className="text-xs text-muted-foreground">
                Tier drift is invisible in a snapshot and obvious in a trend — this is where to see
                it first.
              </p>
            </CardContent>
          </Card>

          {/* Section 3 — All rules at a glance */}
          <Card>
            <CardHeader>
              <CardTitle>All Rules at a Glance</CardTitle>
              <CardDescription>
                Every exception rule this period, sorted by volume and colored by rule severity
                tier. DQ1 and DQ2 are gates, not exceptions, so they are excluded here.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AllRulesBarChart metrics={data.ruleMetrics} definitions={data.ruleDefinitions} />
            </CardContent>
          </Card>

          {/* Section 4 — Tier A */}
          <Card>
            <CardHeader>
              <CardTitle>Tier A: Hard Exceptions</CardTitle>
              <CardDescription>
                Unambiguous policy breaches — rare by design. Hover a rule name for its detection
                logic.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RuleTable
                rows={rowsFor(data, ["A1", "A2", "A3", "A4", "A5"])}
                grid={data.pgFicoGrid}
              />
            </CardContent>
          </Card>

          {/* Section 5 — Tier B */}
          <Card>
            <CardHeader>
              <CardTitle>Tier B: Grid Exceptions</CardTitle>
              <CardDescription>
                Evaluated against the PG FICO grid and the grade/tenure rules.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RuleTable rows={rowsFor(data, ["B1a", "B1b", "B2", "B4"])} grid={data.pgFicoGrid} />
            </CardContent>
          </Card>

          {/* Section 6 — Tier C */}
          <Card>
            <CardHeader>
              <CardTitle>Tier C: Review Flags</CardTitle>
              <CardDescription>
                Softer policy language — triage these, don&rsquo;t auto-fail them.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RuleTable rows={rowsFor(data, ["C1", "C2"])} grid={data.pgFicoGrid} />
            </CardContent>
          </Card>

          <div>
            <Link
              to={`/underwriting-exceptions/exceptions-log${location.search}`}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
            >
              Investigate flagged deals
              <ArrowRight className="size-4" />
            </Link>
          </div>
        </>
      )}
    </StepShell>
  );
}
